import useNotes from "@/src/notes/hooks/useNotes";
import { NoteType } from "@/src/notes/types/NoteType";
import { HeartIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

type LikeButtonProps = {
  note: NoteType;
  disabled: boolean;
  onLike?: (note: NoteType) => void;
};

const LikeButton = ({ note, disabled = false, onLike }: LikeButtonProps) => {
  const { likeNote, unlikeNote, userLikes } = useNotes({
    elementId: note.elementId,
  });

  const isLiked = userLikes?.some((like) => like === note.uid);

  const handleClick = async () => {
    if (isLiked) {
      await unlikeNote(note.uid, {
        optimisticData: (currentData) => {
          return currentData?.filter((uid) => uid !== note.uid);
        },
        rollbackOnError: true,
      });
    } else {
      const likeResponse = await likeNote(note.uid, {
        optimisticData: (currentData) => {
          if (!currentData) {
            return [note.uid];
          }
          return [...currentData, note.uid];
        },
        rollbackOnError: true,
      });

      onLike(likeResponse.like?.note);
    }
  };

  return (
    <button
      className={clsx(
        "group flex items-center space-x-1 text-sm font-normal text-zinc-500 transition ease-out hover:text-zinc-950",
        {
          "pointer-events-none": disabled,
          "text-zinc-950": isLiked,
        }
      )}
      onClick={handleClick}
      disabled={disabled}
      aria-label="Toggle like"
    >
      <div>
        <HeartIcon
          className={clsx("size-4 transition ease-out", {
            "group-hover:fill-zinc-200": !isLiked,
            "fill-zinc-950 hover:fill-zinc-950": isLiked,
          })}
        />
      </div>
      <div className={clsx("lining-nums")}>{note.likesCount || 0}</div>
    </button>
  );
};

export default LikeButton;
